import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCalendarAlt, FaUser, FaTag, FaComment } from 'react-icons/fa';

const BlogDetailSection = styled.section`
  background-color: var(--light-bg);
  padding: 100px 0;
`;

const BlogDetailContainer = styled.div`
  width: 90%;
  max-width: 900px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 30px;
  padding: 0;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--secondary-color);
    transform: translateX(-5px);
  }
`;

const BlogHeader = styled.div`
  margin-bottom: 40px;
`;

const BlogTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BlogMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.9rem;
  
  svg {
    color: var(--primary-color);
  }
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 40px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    height: 250px;
  }
`;

const BlogContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-dark);
  margin-bottom: 50px;
  
  p {
    margin-bottom: 20px;
  }
  
  h2 {
    font-size: 1.8rem;
    font-weight: 600;
    margin: 40px 0 20px;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 30px 0 15px;
  }
  
  ul, ol {
    margin-bottom: 20px;
    padding-left: 20px;
    
    li {
      margin-bottom: 10px;
    }
  }
  
  blockquote {
    border-left: 4px solid var(--primary-color);
    padding-left: 20px;
    font-style: italic;
    margin: 30px 0;
  }
`;

const FeedbackSection = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  margin-top: 60px;
`;

const FeedbackTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 20px;
`;

const FeedbackForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-weight: 500;
  color: var(--text-dark);
`;

const FormInput = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid var(--grey-200);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid var(--grey-200);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  color: white;
  font-weight: 600;
  padding: 14px 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  border: none;
  align-self: flex-start;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(16, 185, 129, 0.1);
  border: 1px solid #10b981;
  color: #065f46;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #b91c1c;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-weight: 500;
`;

const BlogDetail = ({ post, onBack }) => {
  const [feedbackData, setFeedbackData] = useState({
    name: '',
    email: '',
    comment: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: false,
    message: ''
  });
  
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFeedbackData(prev => ({
      ...prev,
      [id]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!feedbackData.name.trim() || !feedbackData.email.trim() || !feedbackData.comment.trim()) {
      setFormStatus({
        submitting: false,
        success: false,
        error: true,
        message: 'Please fill in all required fields'
      });
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(feedbackData.email)) {
      setFormStatus({
        submitting: false,
        success: false,
        error: true,
        message: 'Please enter a valid email address'
      });
      return;
    }
    
    // Set submitting state
    setFormStatus({
      submitting: true,
      success: false,
      error: false,
      message: ''
    });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reset form after successful submission
      setFeedbackData({
        name: '',
        email: '',
        comment: ''
      });
      
      // Set success state
      setFormStatus({
        submitting: false,
        success: true,
        error: false,
        message: 'Thank you for your feedback!'
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, success: false, message: '' }));
      }, 5000);
      
    } catch (error) {
      // Handle error
      setFormStatus({
        submitting: false,
        success: false,
        error: true,
        message: 'Something went wrong. Please try again later.'
      });
    }
  };
  
  if (!post) return null;
  
  return (
    <BlogDetailSection>
      <BlogDetailContainer>
        <BackButton onClick={onBack}>
          <FaArrowLeft /> Back to Blog
        </BackButton>
        
        <BlogHeader>
          <BlogTitle>{post.title}</BlogTitle>
          <BlogMeta>
            <MetaItem>
              <FaCalendarAlt />
              <span>{post.date}</span>
            </MetaItem>
            <MetaItem>
              <FaUser />
              <span>{post.author.name}</span>
            </MetaItem>
            <MetaItem>
              <FaTag />
              <span>{post.category}</span>
            </MetaItem>
          </BlogMeta>
        </BlogHeader>
        
        <FeaturedImage>
          <img src={post.image} alt={post.title} />
        </FeaturedImage>
        
        <BlogContent>
          <p>
            {post.fullContent || `${post.excerpt} ${post.excerpt} This is expanded content for the blog post. In a real implementation, this would be the full article content. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl.`}
          </p>
          
          <h2>Key Takeaways</h2>
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl.
          </p>
          
          <h2>Why This Matters</h2>
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl.
          </p>
          
          <blockquote>
            "This is a quote from an industry expert that adds credibility to the article and provides a different perspective on the topic."
          </blockquote>
          
          <h3>Looking Ahead</h3>
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl. Nullam auctor, nisl eget ultricies tincidunt, nisl nisl aliquam nisl, eget ultricies nisl nisl eget nisl.
          </p>
        </BlogContent>
        
        <FeedbackSection>
          <FeedbackTitle>Leave a Comment</FeedbackTitle>
          
          {formStatus.success && (
            <SuccessMessage>
              {formStatus.message}
            </SuccessMessage>
          )}
          
          {formStatus.error && (
            <ErrorMessage>
              {formStatus.message}
            </ErrorMessage>
          )}
          
          <FeedbackForm onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel htmlFor="name">Name *</FormLabel>
              <FormInput 
                type="text" 
                id="name" 
                value={feedbackData.name}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="email">Email *</FormLabel>
              <FormInput 
                type="email" 
                id="email" 
                value={feedbackData.email}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="comment">Comment *</FormLabel>
              <FormTextarea 
                id="comment" 
                value={feedbackData.comment}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <SubmitButton 
              type="submit" 
              disabled={formStatus.submitting}
            >
              {formStatus.submitting ? 'Submitting...' : 'Submit Comment'}
            </SubmitButton>
          </FeedbackForm>
        </FeedbackSection>
      </BlogDetailContainer>
    </BlogDetailSection>
  );
};

export default BlogDetail;
